import classNames from 'classnames';
import { useEffect, useState } from 'react';
import Image from '@/components/atoms/image/image';
import RichText from '@/components/atoms/rich-text/rich-text';
import Typography from '@/components/atoms/typography/typography';
import Button from '@/components/molecules/button/button';
import Tag from '@/components/molecules/tag/tag';
import FavoriteHeart from '@/components/organisms/favorite-heart/favorite-heart';
import { getTeaserCardImageConfig } from '@/components/organisms/teaser-card/teaser-card.utils';
import Link from '@/components/utilities/link/link';
import { useFavoritesContext } from '@/libraries/contexts/favorites-context';
import { useI18n } from '@/libraries/contexts/i18n-context';
import useCurrencyFormatter from '@/libraries/formatters/use-currency-formatter';
import useIsHovered from '@/libraries/hooks/use-is-hovered';
import styles from './teaser-card.module.scss';

export interface TeaserCardProps {
  /** The class name to apply */
  className?: string;
  /** The description of the card */
  description?: Content.Components['TeaserGridItemDto']['description'];
  /** Optional fixed aspect ratio used to avoid post-hydration resize shifts */
  fixedImageAspectRatio?: Content.Properties.AspectRatio;
  /** The headline of the card */
  headline: Content.Components['TeaserGridItemDto']['headline'];
  /** The background image of the card */
  image: Content.Components['TeaserGridItemDto']['image'];
  /** Number of cards rendered in the current row */
  itemsInRow?: number;
  /** The link to show */
  link?: Content.Components['TeaserGridItemDto']['link'];
  /** Tag to display on the card */
  tag?: {
    text: string;
  };
  /** From price to show on the card */
  totalPrice?: Content.Components['TeaserGridItemDto']['totalPrice'];
  /** The unique id for the offer */
  uniqueId?: Content.Components['TeaserGridItemDto']['uniqueId'];
}

const MIRRORED_IMAGE_SIZE = 96;

const TeaserCard = ({
  className,
  description,
  fixedImageAspectRatio,
  headline,
  image,
  itemsInRow,
  link,
  tag,
  totalPrice,
  uniqueId,
}: TeaserCardProps) => {
  const { t } = useI18n();
  const currencyFormatter = useCurrencyFormatter();
  const {
    bindRef,
    elementRef: componentRef,
    isHovered,
  } = useIsHovered<HTMLAnchorElement>();
  const [imageAspectRatio, setImageAspectRatio] =
    useState<Content.Properties.AspectRatio>(
      fixedImageAspectRatio ?? 'fourByThree',
    );

  const { addFavoriteOffer, isFavorite, removeFavoriteOffer } =
    useFavoritesContext();

  const isOffer = !!uniqueId;
  const teaserCardImageConfig = getTeaserCardImageConfig(itemsInRow);

  const handleClickFavorite = () => {
    if (!uniqueId) return;

    if (isFavorite(uniqueId)) {
      removeFavoriteOffer(uniqueId);
    } else {
      addFavoriteOffer(uniqueId);
    }
  };

  useEffect(() => {
    if (fixedImageAspectRatio) {
      setImageAspectRatio(fixedImageAspectRatio);

      return;
    }

    const componentElement = componentRef.current;

    if (!componentElement) return;

    const handleResize = () => {
      const { width } = componentElement.getBoundingClientRect();

      if (width >= 450) {
        setImageAspectRatio('sixteenByNine');
      } else if (width >= 330) {
        setImageAspectRatio('oneByOne');
      } else {
        setImageAspectRatio('fourByThree');
      }
    };

    handleResize();

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [componentRef, fixedImageAspectRatio]);

  return (
    <Link
      ref={bindRef}
      className={classNames(styles.component, className, {
        [styles.isOffer]: isOffer,
      })}
      url={link ? link.url : undefined}
    >
      {isOffer && uniqueId && (
        <FavoriteHeart
          className={styles.favorite}
          isFavorite={isFavorite(uniqueId)}
          onClickFavorite={() => handleClickFavorite()}
        />
      )}

      <div className={styles.imageWrapper}>
        <Image
          alt={image.altText}
          aspectRatio={imageAspectRatio}
          className={styles.image}
          focalPoint={image.focalPoint}
          height={458}
          src={image.url}
          srcSet={teaserCardImageConfig.srcSet}
          unoptimized
          width={teaserCardImageConfig.width}
        />
      </div>

      <div className={styles.mirroredImageWrapper}>
        <Image
          alt=""
          aria-hidden
          aspectRatio={imageAspectRatio}
          className={styles.mirroredImage}
          fill
          focalPoint={image.focalPoint}
          src={image.url}
          unoptimized
          width={MIRRORED_IMAGE_SIZE}
        />

        <div className={styles.contentWrapper}>
          <div className={styles.topSection}>
            <div className={styles.details}>
              <Typography
                className={styles.headline}
                tag="h3"
                tagStyle="headingMedium"
              >
                {headline}
              </Typography>

              {description && (
                <RichText className={styles.description} text={description} />
              )}
            </div>

            {tag && (
              <Tag
                className={styles.tag}
                text={tag.text}
                variant={'secondary'}
              />
            )}
          </div>

          <div className={styles.bottomSection}>
            {totalPrice && (
              <div className={styles.fromPrice}>
                <Typography tagStyle="priceMedium">
                  {`${t('items.common.words.from')} ${currencyFormatter(totalPrice)}`}
                </Typography>
              </div>
            )}

            {link && (
              <div className={styles.buttonWrapper}>
                <Button
                  aria-label={link?.text}
                  className={styles.smallButton}
                  hovered={isHovered}
                  icon="ChevronRight"
                  tabIndex={-1}
                  variant="primary"
                />

                <Button
                  className={styles.defaultButton}
                  hovered={isHovered}
                  tabIndex={-1}
                  text={link?.text}
                  variant="primary"
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </Link>
  );
};

TeaserCard.displayName = 'TeaserCard';

export default TeaserCard;
